import * as React from "react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { PineScriptCodeBlock } from "../Reports/PineScriptCodeBlock";
import type { ComposeInjectionPreview } from "../../types/reportStrategyPine";
import { formatInjectionSiteLabel } from "../../utils/reportStrategyPineUi";
import { REPORT_DETAIL_CARD_BORDER } from "../../views/Dashboard/Reports/components/ReportsCardChrome";
import { STRATEGY_PINE_BADGE_CLASS } from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";

/** Compact height for before/after snippets inside the compose card */
const PINE_SCRIPT_INJECTION_PREVIEW_MAX_H = "min(28vh, 240px)";

function InjectionPreviewPinePanel({
  label,
  code,
}: {
  label: string;
  code: string;
}): React.ReactElement {
  return (
    <Field className="w-full" data-strategy-compose-injection-preview-pine="">
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{label}</FieldLabel>
      <PineScriptCodeBlock
        code={code}
        maxH={PINE_SCRIPT_INJECTION_PREVIEW_MAX_H}
        brandChrome
        chromeTitle={label}
        chromeSubtitle="Generated code"
      />
    </Field>
  );
}

export type StrategyComposeInjectionPreviewCardProps = {
  preview: ComposeInjectionPreview;
  whatHappens?: string | null;
  /** Raw BE injection_site id (e.g. entry_calls, separate_generated_file) */
  injectionSite?: string | null;
  injectionSiteUx?: Record<string, string | { label?: string; description?: string }> | null;
};

export function StrategyComposeInjectionPreviewCard({
  preview,
  whatHappens,
  injectionSite,
  injectionSiteUx,
}: StrategyComposeInjectionPreviewCardProps): React.ReactElement {
  const injectionSiteLabel = formatInjectionSiteLabel(injectionSite, injectionSiteUx);

  return (
    <Card
      data-strategy-compose-injection-preview=""
      size="sm"
      className="gap-0 rounded-[10px] py-0 shadow-none ring-0"
      style={{ border: `1px solid ${REPORT_DETAIL_CARD_BORDER}` }}
    >
      <CardHeader className="grid-cols-1 gap-2 px-3 py-3 has-data-[slot=card-action]:grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-start">
        <div className="flex min-w-0 flex-col gap-1 text-left">
          <CardTitle className={`${STRATEGY_PINE_SECTION_LABEL_CLASS} text-sm font-normal`}>
            Compose injection preview
          </CardTitle>
          {whatHappens ? (
            <CardDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{whatHappens}</CardDescription>
          ) : null}
        </div>
        {injectionSiteLabel ? (
          <CardAction className="col-start-1 row-start-auto w-full sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:w-auto sm:justify-self-end">
            <Badge
              variant="pine-meta"
              data-strategies-pine-badge={
                injectionSite ? `injection-${injectionSite}` : "injection-site"
              }
              data-strategy-compose-injection-site={injectionSite ?? undefined}
              className={STRATEGY_PINE_BADGE_CLASS}
            >
              {injectionSiteLabel}
            </Badge>
          </CardAction>
        ) : null}
      </CardHeader>

      {preview.before || preview.after ? (
        <CardContent className="flex flex-col gap-4 px-3 pb-3 pt-0">
          {preview.before ? <InjectionPreviewPinePanel label="Before" code={preview.before} /> : null}
          {preview.after ? <InjectionPreviewPinePanel label="After" code={preview.after} /> : null}
        </CardContent>
      ) : null}
    </Card>
  );
}
